import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, Select, InputNumber, Upload, Button, Row, Col, message, Modal, Typography, Switch } from 'antd';
import * as AntDIcons from '@ant-design/icons';
import SimpleBarReact from 'simplebar-react';
import StickyBox from 'react-sticky-box';
import DocumentViewer from '../../../components/document-viewer/DocumentViewer';
import {
   getCourses,
   getTopicsByCourse,
} from '../../../services/MicrocredentialsCourseTopicService';
import WysiwygMathEditor from '../../../components/wysiwyg/WysiwygMathEditor';
import {
   getMicrocredentialsTaskModule,
   detectMicrocredentialsTaskMode,
   MICROCREDENTIALS_TASK_MODES,
} from '../../../config/microcredentialsTaskModules';
import { isPlagiarismCheckerEnabled } from '../../HomeworksPage/plagiarismUtils';

const { Text } = Typography;
const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_MB = 10;
const LOCAL_SUBDOMAIN = localStorage.getItem('lms-subdomain');

function parseExistingDocuments(documentValue) {
   if (!documentValue) return [];

   try {
      const parsed = JSON.parse(documentValue);
      if (Array.isArray(parsed)) {
         return parsed
            .filter((item) => item && item.s3_path)
            .map((item, index) => ({
               uid: `existing-${index}`,
               name: item.original_filename || item.s3_path.split('/').pop() || `Attachment ${index + 1}`,
               status: 'done',
               url: resolveStoredDocumentUrl(item.s3_path),
               isExisting: true,
               documentInfo: {
                  original_filename: item.original_filename || item.s3_path.split('/').pop() || '',
                  extension_name: item.extension_name || '',
                  s3_path: item.s3_path,
               },
             }));
      }
   } catch (error) {
      // Fall back to legacy comma-separated values.
   }

   return String(documentValue)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((path, index) => ({
         uid: `existing-${index}`,
         name: path.split('/').pop() || path,
         status: 'done',
         url: resolveStoredDocumentUrl(path),
         isExisting: true,
         documentInfo: {
            original_filename: path.split('/').pop() || path,
            extension_name: `.${(path.split('.').pop() || '').toLowerCase()}`.replace(/\.$/, ''),
            s3_path: path,
         },
      }));
}

function resolveStoredDocumentUrl(storedPath) {
   const raw = String(storedPath || '').trim();
   if (!raw) return null;
   if (/^(https?:)?\/\//i.test(raw) || /^(data|blob):/i.test(raw)) return raw;
   if (raw.startsWith('/')) return `${process.env.REACT_APP_API_S3_URL}${raw}`;

   if (LOCAL_SUBDOMAIN && raw.startsWith(`${LOCAL_SUBDOMAIN}/`)) {
      return `${process.env.REACT_APP_API_S3_URL}/${raw}`;
   }

   return `${process.env.REACT_APP_API_S3_URL}/${raw}`;
}

export default function PerformanceTaskForm({
   formId,
   initialValues,
   onSubmit,
   loading,
   forcedCourseId,
   disableTopicSelection = false,
   readOnly = false,
   mode = detectMicrocredentialsTaskMode(window.location?.pathname),
}) {
   const moduleConfig = getMicrocredentialsTaskModule(mode);
   const taskLabel = moduleConfig.label;
   const taskLabelLower = taskLabel.toLowerCase();

   const [form] = Form.useForm();
   const [courses, setCourses] = useState([]);
   const [topics, setTopics] = useState([]);
   const [coursesLoading, setCoursesLoading] = useState(false);
   const [topicsLoading, setTopicsLoading] = useState(false);
   const [files, setFiles] = useState([]);
   const [removedExistingFiles, setRemovedExistingFiles] = useState([]);
   const [previewDocument, setPreviewDocument] = useState(null);
   const [previewDocumentName, setPreviewDocumentName] = useState(null);
   const [previewModalVisible, setPreviewModalVisible] = useState(false);

   const getCourseId = (course) => course?.course_id;
   const getTopicId = (topic) => topic?.topic_id;

   const selectedCourseId = Form.useWatch('course_id', form);
   const descriptionValue = Form.useWatch('description', form) || '';

   const fetchTopics = async (courseId) => {
      if (!courseId) {
         setTopics([]);
         return;
      }

      try {
         setTopicsLoading(true);
         const data = await getTopicsByCourse(courseId);
         setTopics(Array.isArray(data) ? data : []);
      } catch (error) {
         message.error('Failed to load topics');
         setTopics([]);
      } finally {
         setTopicsLoading(false);
      }
   };

   useEffect(() => {
      const loadCourses = async () => {
         try {
            setCoursesLoading(true);
            const data = await getCourses();
            setCourses(Array.isArray(data) ? data : []);
         } catch (error) {
            message.error('Failed to load courses');
         } finally {
            setCoursesLoading(false);
         }
      };
      loadCourses();
   }, []);

   useEffect(() => {
      if (!selectedCourseId) {
         setTopics([]);
         form.setFieldsValue({ topic_id: undefined });
         return;
      }

      fetchTopics(selectedCourseId);
   }, [form, selectedCourseId]);

   useEffect(() => {
      const initialCourseId =
         initialValues?.Course_id ??
         initialValues?.course_id ??
         initialValues?.courseId ??
         initialValues?.course?.course_id;

      const initialTopicId =
         initialValues?.Topic_id ??
         initialValues?.topic_id ??
         initialValues?.topicId ??
         initialValues?.topic?.topic_id;

      form.setFieldsValue({
         title: initialValues?.title || '',
         description: initialValues?.description || '',
         course_id: forcedCourseId || initialCourseId || undefined,
         topic_id: initialTopicId || undefined,
         max_points: initialValues?.max_points ?? undefined,
         plagiarism_checker: isPlagiarismCheckerEnabled(
            initialValues?.plagiarism_checker ??
            initialValues?.plagiarismChecker ??
            initialValues?.Plagiarism_checker
         ),
      });

      setFiles(parseExistingDocuments(initialValues?.document));
      setRemovedExistingFiles([]);

      if (forcedCourseId || initialCourseId) {
         fetchTopics(forcedCourseId || initialCourseId);
      }
   }, [forcedCourseId, form, initialValues]);

   useEffect(() => () => {
      if (previewDocument && previewDocument.startsWith('blob:')) {
         URL.revokeObjectURL(previewDocument);
      }
   }, [previewDocument]);

   const handlePreviewAttached = useCallback((file) => {
      if (previewDocument && previewDocument.startsWith('blob:')) {
         URL.revokeObjectURL(previewDocument);
      }

      if (file?.isExisting) {
         setPreviewDocument(file.url || resolveStoredDocumentUrl(file.documentInfo?.s3_path));
         setPreviewDocumentName(file.name || file.documentInfo?.original_filename || 'Attachment');
         setPreviewModalVisible(true);
         return;
      }

      const sourceFile = file.originFileObj || file;
      const objectUrl = URL.createObjectURL(sourceFile);
      setPreviewDocument(objectUrl);
      setPreviewDocumentName(file.name || sourceFile.name);
      setPreviewModalVisible(true);
   }, [previewDocument]);

   const handleClosePreview = () => {
      setPreviewModalVisible(false);
      setPreviewDocument((currentDocument) => {
         if (currentDocument && currentDocument.startsWith('blob:')) {
            URL.revokeObjectURL(currentDocument);
         }

         return null;
      });
      setPreviewDocumentName(null);
   };

   const handleDescriptionChange = (value) => {
      form.setFieldsValue({ description: value });
   };

   const uploadProps = useMemo(() => ({
      multiple: true,
      fileList: files,
      beforeUpload: (file) => {
         if (readOnly) {
            return Upload.LIST_IGNORE;
         }

         const isWithinSize = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB;

         if (!isWithinSize) {
            Modal.error({
               title: taskLabel,
               content: `Maximum allowed file size is ${MAX_FILE_SIZE_MB} MB per file.`,
               centered: true,
            });
            return Upload.LIST_IGNORE;
         }

         if (files.length >= MAX_ATTACHMENTS) {
            Modal.error({
               title: taskLabel,
               content: `You can only upload up to ${MAX_ATTACHMENTS} files.`,
               centered: true,
            });
            return Upload.LIST_IGNORE;
         }

         setFiles((prev) => (prev.length >= MAX_ATTACHMENTS ? prev : [...prev, file]));
         return false;
      },
      onRemove: (file) => {
         if (readOnly) {
            return false;
         }

         if (file.isExisting && file.documentInfo) {
            setRemovedExistingFiles((prev) => [...prev, file.documentInfo]);
         }
         setFiles((prev) => prev.filter((item) => item.uid !== file.uid));
      },
      onPreview: (file) => {
         handlePreviewAttached(file);
      },
      disabled: readOnly || files.length >= MAX_ATTACHMENTS,
      showUploadList: {
         showPreviewIcon: true,
         showRemoveIcon: !readOnly,
      },
   }), [files, handlePreviewAttached, readOnly, taskLabel]);

   const handleFinish = async (values) => {
      const payload = {
         ...values,
         course_id: forcedCourseId || values.course_id,
         Task_type: moduleConfig.taskType,
         document: JSON.stringify(
            files
               .filter((file) => file.isExisting && file.documentInfo)
               .map((file) => file.documentInfo)
         ),
         removed_documents: JSON.stringify(removedExistingFiles),
      };

      if (typeof onSubmit === 'function') {
         await onSubmit(payload, files.filter((file) => !file.isExisting));
      }
   };

   return (
      <Form
         id={formId}
         className="homework-modal-form"
         form={form}
         layout="vertical"
         colon={false}
         onFinish={readOnly ? undefined : handleFinish}
      >
         <div className="homework-modal-section">
            <div className="homework-modal-section-header">
               <span className="homework-modal-section-icon"><AntDIcons.SettingOutlined /></span>
               <div>
                  <span className="homework-modal-kicker">Details</span>
                  <h3>{taskLabel} Settings</h3>
               </div>
            </div>

            <Row gutter={16}>
               <Col xs={24} md={14} lg={14}>
                  <Form.Item name="title" label={<Text>Title</Text>} rules={[{ required: true, message: 'Title is required' }]}>
                     <Input
                        allowClear
                        size="middle"
                        style={{ borderRadius: 7 }}
                        placeholder={`Enter ${taskLabelLower} title`}
                        maxLength={250}
                        disabled={readOnly}
                     />
                  </Form.Item>
               </Col>
               <Col xs={24} md={5} lg={5}>
                  <Form.Item name="max_points" label={<Text>Max Score</Text>} rules={[{ required: true, message: 'Please enter max score' }]}>
                     <InputNumber
                        min={0}
                        size="middle"
                        style={{ borderRadius: 7, width: '100%' }}
                        placeholder="Enter max score"
                        disabled={readOnly}
                     />
                  </Form.Item>
               </Col>
               <Col xs={24} md={5} lg={5}>
                  <Form.Item
                     name="plagiarism_checker"
                     label={<Text>Plagiarism Checker</Text>}
                     valuePropName="checked"
                     initialValue={false}
                     style={{ marginBottom: 4 }}
                  >
                     <Switch checkedChildren="On" unCheckedChildren="Off" disabled={readOnly} />
                  </Form.Item>
                  {/* <Text type="secondary">Grade 9 to Grade 12 only.</Text> */}
               </Col>
            </Row>

            <Row gutter={16}>
               <Col xs={24} md={12} lg={12}>
                  <Form.Item name="course_id" label={<Text>Course</Text>} rules={[{ required: true, message: 'Course is required' }]}>
                     <Select
                        showArrow
                        showSearch
                        allowClear
                        loading={coursesLoading}
                        size="middle"
                        style={{ width: '100%' }}
                        placeholder="Select Course"
                        optionFilterProp="label"
                        onChange={() => {
                           form.setFieldsValue({ topic_id: undefined });
                        }}
                        disabled={readOnly || Boolean(forcedCourseId)}
                        options={courses.map((course) => ({
                           value: getCourseId(course),
                           label: course.name,
                        }))}
                     />
                  </Form.Item>
               </Col>
               <Col xs={24} md={12} lg={12}>
                  <Form.Item name="topic_id" label={<Text>Topic</Text>} rules={[{ required: true, message: 'Topic is required' }]}>
                     <Select
                        showArrow
                        showSearch
                        allowClear
                        loading={topicsLoading}
                        size="middle"
                        style={{ width: '100%' }}
                        placeholder="Select Topic"
                        optionFilterProp="label"
                        disabled={readOnly || disableTopicSelection || !selectedCourseId}
                        options={topics.map((topic) => ({
                           value: getTopicId(topic),
                           label: topic.title || topic.name,
                        }))}
                     />
                  </Form.Item>
               </Col>
            </Row>

         </div>

         <Row gutter={16} className="homework-modal-panel-grid">
            <Col xs={24} md={16} lg={16}>
               <div className="homework-modal-panel">
                  <div className="homework-modal-section-header">
                     <span className="homework-modal-section-icon"><AntDIcons.FileTextOutlined /></span>
                     <div>
                        <span className="homework-modal-kicker">Content</span>
                        <h3>Description</h3>
                     </div>
                  </div>
                  <Form.Item
                     label={<Text>Description</Text>}
                     required
                     validateStatus={form.getFieldError('description').length > 0 ? 'error' : ''}
                     help={form.getFieldError('description')[0]}
                  >
                     <Form.Item name="description" rules={[{ required: true, message: 'Description is required' }]} hidden>
                        <Input />
                     </Form.Item>
                     <WysiwygMathEditor
                        id={`performance-task-description-${formId || 'form'}`}
                        content={descriptionValue}
                        changeContentCallback={handleDescriptionChange}
                        height={260}
                        disabled={readOnly}
                     />
                  </Form.Item>
               </div>
            </Col>

            <Col xs={24} md={8} lg={8}>
               <div className="homework-modal-panel">
                  <div className="homework-modal-section-header">
                     <span className="homework-modal-section-icon"><AntDIcons.CloudUploadOutlined /></span>
                     <div>
                        <span className="homework-modal-kicker">Resources</span>
                        <h3>Attach Documents</h3>
                     </div>
                  </div>
                  <Form.Item label={<Text>{`Attach Document(s) (max ${MAX_ATTACHMENTS} files, ${MAX_FILE_SIZE_MB} MB each)`}</Text>}>
                     <SimpleBarReact className="homework-modal-scroll">
                        <StickyBox>
                           <Upload
                              style={{ display: 'block' }}
                              multiple
                              {...uploadProps}
                              listType="picture"
                              className="upload-list-inline"
                              accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xls,.xlsx,.png,.jpg,.pdf,.ppsx"
                           >
                              {!readOnly && (
                                 <Button size="middle" shape="round" icon={<AntDIcons.CloudUploadOutlined />} block disabled={readOnly || files.length >= MAX_ATTACHMENTS}>
                                    Select Files
                                 </Button>
                              )}
                           </Upload>
                        </StickyBox>
                     </SimpleBarReact>
                  </Form.Item>
               </div>
            </Col>
         </Row>

         {!readOnly && (
            <div style={{ display: 'none' }}>
               <Button htmlType="submit" loading={loading}>Submit</Button>
            </div>
         )}

         <Modal
            title="Document Preview"
            className="homework-assignment-modal homework-document-preview-modal"
            centered
            destroyOnClose
            open={previewModalVisible}
            width="80%"
            footer={[
               <Button key="close" shape="round" onClick={handleClosePreview}>
                  Close
               </Button>
            ]}
            onCancel={handleClosePreview}
         >
            <div className="modal-document-viewer homework-document-preview-viewer">
               <DocumentViewer
                  document={previewDocument}
                  documentname={previewDocumentName}
                  dvheight="calc(100dvh - 220px)"
                  ismodal={true}
               />
            </div>
         </Modal>
      </Form>
   );
}

